import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {TabButtonElement} from '../../analytics/components/tabs/tabButtonElement';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {FolderBreadcrumb} from '../../components/folderBreadcrumbs/folderBreadcrumbs';
import {findParentTags} from '../../helpers/models/tagHelpers';
import {makeMapStateToProps, ResolveStateProps} from '../../helpers/reselectHelpers';
import {TagModel} from '../../models/api/tagModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {
  SettingsResourcePagesEnum,
  SettingsTabsPagesEnum,
  SettingsTagsUrl,
} from '../../models/url/settingsUrlModel';
import {AppState} from '../../store/appState';
import {getTags} from '../../store/data/tags/tagsSelectors';
import {SettingsBreadcrumbs} from '../common/settingsBreadcrumbs';
import {SettingsHeader} from '../settingsHeader';
import {SettingsTagsBreadcrumbItem} from './settingsTagsBreadcrumbItem';
import {SettingsTagsHeaderTitle} from './settingsTagsHeaderTitle';

/*
 * Props.
 */

interface SettingsTagsHeaderContainerProps {
  tagId?: number;
  tagsUrl?: SettingsTagsUrl;
  parentTag?: TagModel;
  right?: ReactNode;
  isEditingATag?: boolean;
}

/*
 * Selectors.
 */

function getTagId(_state: AppState, props: SettingsTagsHeaderContainerProps) {
  return props.tagId;
}

function getParentTag(_state: AppState, props: SettingsTagsHeaderContainerProps) {
  return props.parentTag;
}

function makeGetTag() {
  return createSelector([getTagId, getTags], (tagId, tags) => (tagId ? tags[tagId] : undefined));
}

function makeGetParentTags(getTag: ReturnType<typeof makeGetTag>) {
  return createSelector([getTag, getTags, getParentTag], (tag, tags, parentTag) => {
    if (!tag && parentTag) {
      return [parentTag, ...findParentTags(tags, parentTag)].reverse();
    }

    if (!tag) {
      return undefined;
    }

    return [...findParentTags(tags, tag)].reverse();
  });
}

/*
 * Component.
 */

interface SettingsTagsHeaderProps
  extends SettingsTagsHeaderContainerProps,
    ResolveStateProps<typeof mapStateToProps> {
  openParentTag: (tagId?: number) => void;
}

const SettingsTagsHeader: FC<SettingsTagsHeaderProps> = (props) => {
  const {tag, tagsUrl, parentTags, openParentTag, isEditingATag} = props;
  const {navigateToLink} = useLinkNavigation();

  const renderTopLine = () => {
    if (tagsUrl?.resourcePage === SettingsResourcePagesEnum.OVERVIEW && !parentTags) {
      return null;
    }

    const folderPathBreadcrumbs: ReadonlyArray<FolderBreadcrumb> = (parentTags || []).map((parentTag) => ({
      label: <SettingsTagsBreadcrumbItem tag={parentTag} />,
      onBreadcrumbClick: () => openParentTag(parentTag.id),
    }));

    return (
      <SettingsBreadcrumbs
        showSeparatorAfterTopLevel
        separatorIsHighlighted={folderPathBreadcrumbs.length > 0}
        topLevelBreadcrumb={{
          label: <Trans id="settings-tags">Tags</Trans>,
          onBreadcrumbClick: () => openParentTag(),
        }}
        breadcrumbs={folderPathBreadcrumbs}
      />
    );
  };

  const renderTabs = () => {
    if (isEditingATag || !tagsUrl) {
      return null;
    }

    return (
      <>
        <TabButtonElement
          interactionId="settingtags-tab-all"
          name={<Trans id="settingstags-all">All tags</Trans>}
          isSelected={tagsUrl.tabPage === SettingsTabsPagesEnum.ALL}
          onTabClick={() => {
            navigateToLink(
              buildSettingsPath({
                ...tagsUrl,
                tabPage: SettingsTabsPagesEnum.ALL,
              }),
            );
          }}
        />
        <TabButtonElement
          interactionId="settingtags-tab-archived"
          name={<Trans id="settingstags-archived">Archived</Trans>}
          isSelected={tagsUrl.tabPage === SettingsTabsPagesEnum.ARCHIVED}
          onTabClick={() => {
            navigateToLink(
              buildSettingsPath({
                ...tagsUrl,
                tabPage: SettingsTabsPagesEnum.ARCHIVED,
              }),
            );
          }}
        />
      </>
    );
  };

  return (
    <SettingsHeader right={props.right} topLine={renderTopLine()} tabs={renderTabs()}>
      <SettingsTagsHeaderTitle isEditingATag={props.isEditingATag} tag={tag} />
    </SettingsHeader>
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SettingsTagsHeaderContainerProps>()(() => {
  const getTag = makeGetTag();
  return {
    tag: getTag,
    parentTags: makeGetParentTags(getTag),
  };
});

export const SettingsTagsHeaderContainer = connect(mapStateToProps)(SettingsTagsHeader);
